import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Button from "designSystem/components/button/Button";
import FlexRowCenter from "designSystem/components/flex/FlexRowCenter";
import { useLocale } from "designSystem/locale";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import _ from "lodash";
import React from "react";
import { useErrorBoundary } from "react-error-boundary";
import { SelectedChannel } from "../../types";
import * as api from "../api";
import ConnectToSlackFirstTime from "./ConnectToSlackFirstTime";
import LoadingSlack from "./LoadingSlack";
import ShowAllConnectedChannels from "./ShowAllConnectedChannels";

const PREFIX = "ConnectToSlackNextTime";

const classes = {
	root: `${PREFIX}root`,
	channels: `${PREFIX}channels`,
};

const StyledGrid = styled(Grid)({
	[`&.${classes.root}`]: {
		height: "100%",
	},
	[`& .${classes.channels}`]: {
		boxSizing: "border-box",
		flex: 1,
		minHeight: 0,
		height: "100%",
		overflowY: "scroll",
		overflowX: "hidden",
		width: "100%",
	},
});

interface Props {
	nodeType: string;
	isNodeReadOnly: boolean;
	nodeSlackChannels: string[];
	onConnectToSlack?: () => void;
	onSelectChannel?: (channel: SelectedChannel) => void;
}

function sortChannels(channels: api.Conversation[]) {
	channels.sort((c1, c2) => c1.name.localeCompare(c2.name));
}

const ConnectToSlackNextTime: React.FunctionComponent<Props> = ({ nodeType, isNodeReadOnly, nodeSlackChannels, onConnectToSlack, onSelectChannel }) => {
	class AvailableChannels {
		constructor(readonly myChannels: api.Conversation[], readonly otherConnectedChannels: api.Conversation[]) { }

		isEmpty() {
			return this.myChannels.length == 0 && this.otherConnectedChannels.length == 0;
		}
	};


	const locale = useLocale();
	const resParams = [locale.type(nodeType)];

	const [availableChannels, setAvailableChannels] = React.useState<AvailableChannels>(null);
	const { showBoundary } = useErrorBoundary();

	React.useEffect(() => {
		async function fetchChannels() {
			try {
				const channelsJson = await api.getListOfChannels();
				const userConversationsJson = await api.getUserConversations();

				// convert channel Ids into full Conversation objects and
				// group them by the condition: if the user is a member of the channel
				let { true: myChannels, false: otherConnectedChannels } = _.groupBy(
					nodeSlackChannels
						.map((channelId) => channelsJson.channels.find((c) => c.id == channelId))
						.filter(Boolean),
					(conversation) => userConversationsJson.channels.some((c) => conversation.id == c.id),
				);
				sortChannels(myChannels ??= []);
				sortChannels(otherConnectedChannels ??= []);
				setAvailableChannels(new AvailableChannels(myChannels, otherConnectedChannels));
			} catch (error) {
				showBoundary(error);
			}
		}
		fetchChannels();
	}, []);

	if (null == availableChannels) {
		return <LoadingSlack />;
	}

	// show 'No Channels connected' message for read-only users if there are no available channels for this C8 node and user
	if (isNodeReadOnly && availableChannels.isEmpty()) {
		return (
			<ConnectToSlackFirstTime
				isNodeReadOnly={isNodeReadOnly}
				nodeType={nodeType}
				onConnectToSlack={onConnectToSlack}
			/>
		);
	}

	return (
		<StyledGrid
			alignItems="stretch"
			className={classes.root}
			container
			direction="column"
			justifyContent="flex-start"
		>
			<Grid className={classes.channels} item>
				<ShowAllConnectedChannels
					myChannels={availableChannels.myChannels}
					onSelectChannel={onSelectChannel}
					otherConnectedChannels={availableChannels.otherConnectedChannels}
				/>
			</Grid>
			{
				isNodeReadOnly ? null : (
					<FlexRowCenter
						borderTop={STANDARD_BORDER_STYLE}
						justifyContent="center"
						p={2}
					>
						<Button
							design="primary"
							fullWidth
							onClick={onConnectToSlack}
							res="ConnectToSlackButton"
							resParams={resParams}
						/>
					</FlexRowCenter>
				)}
		</StyledGrid>
	);
};

export default ConnectToSlackNextTime;